import {
  Errors,
  ErrorTypes,
  flattenErrorTypeArray,
  groupErrorsByNonProperty,
  Table,
  TableProps,
  useStyledTheme,
  useWindowDimensions,
} from '@equifinance/shared';
import merge from 'lodash/merge';
import { useMemo } from 'react';
import { css, CSSObject } from 'styled-components';
import {
  CreditItem,
  IgnoredByType,
  Insolvency,
  useCreditContext,
  checkCreditItemIsSettled, checkInsolvencyIsSettled,
} from '../../contexts/credit';

const tableContainerCss = css`
  >div {
    overflow-x: scroll;
    >div {
      min-width: 100%;
      width: auto;
    }
  }
`;

const stickyHead = {
  style: {
    position: 'unset',
    top: 0,
    zIndex: 0,
  } as CSSObject,
};

const MIN_APP_WIDTH = 1280;

export const creditLineErrorTypes: (keyof typeof ErrorTypes)[] = [
  ErrorTypes.RejectData,
  ErrorTypes.ValidationFailure,
];

export const creditLineWarningTypes: (keyof typeof ErrorTypes)[] = [
  ErrorTypes.Warning,
  ErrorTypes.Refer,
];

function hasErrorOrWarningOfTypes(
  errors: Errors,
  warnings: Errors,
  id: string,
  errorTypes: (keyof typeof ErrorTypes)[],
): boolean {
  const itemErrorsAndWarnings = flattenErrorTypeArray(
    merge({}, errors, warnings)[id],
  );

  const filteredErrors = groupErrorsByNonProperty(itemErrorsAndWarnings);

  return errorTypes.some((type) => (
    (filteredErrors?.[type]?.length ?? 0) > 0
  ));
}

export default function CreditTable<T extends CreditItem | Insolvency>({
  ...props
}: TableProps<T>) {
  const theme = useStyledTheme();
  const { credit: { errors, warnings } } = useCreditContext();

  const { width } = useWindowDimensions();
  const isMinAppWidth = width <= MIN_APP_WIDTH;
  const containerCss = useMemo(() => (isMinAppWidth ? tableContainerCss : ''), [width]);
  const stickyHeadStyle = useMemo(() => (isMinAppWidth ? stickyHead : { style: {} }), [width]);

  return (
    <Table
      {...props}
      containerCss={containerCss}
      conditionalRowStyles={[
        {
          when: (row: any) => hasErrorOrWarningOfTypes(errors, warnings, row?.id, creditLineWarningTypes),
          style: {
            backgroundColor: `${theme.colours.secondary[6]} !important`,
          },
        },
        {
          when: (row: any) => hasErrorOrWarningOfTypes(errors, warnings, row?.id, creditLineErrorTypes),
          style: {
            backgroundColor: `${theme.colours.danger[2]} !important`,
          },
        },
        {
          when: (row) => {
            const { ignored, ignoredBy } = row;
            const isSystemIgnored = ignored && ignoredBy === IgnoredByType.System;
            const isCreditItemIsSettled = (checkCreditItemIsSettled(row as CreditItem) || checkInsolvencyIsSettled(row as Insolvency)) && !ignored;
            return isSystemIgnored || isCreditItemIsSettled;
          },
          style: {
            backgroundColor: `${theme.colours.neutral[8]} !important`,
          },
        },
      ]}
      styles={{
        rows: {
          style: {
            backgroundColor: `${theme.colours.primary[11]} !important`,
            fontSize: `${theme.fontSize[1]} !important`,
            '&:not(:last-of-type)': {
              borderBottomWidth: `${0} !important`,
            },
          },
        },
        head: stickyHeadStyle,
      }}
    />
  );
}
